import React from 'react';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useMemoryContext } from '@/components/MemoryWatchdogProvider';
import { useSuperAdmin } from '@/hooks/useSuperAdmin';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/**
 * Small floating badge showing JS heap memory usage.
 * Only visible to super admins (Chrome only - performance.memory).
 */
export const MemoryUsageBadge: React.FC = () => {
  const { memoryUsage, forceCleanup } = useMemoryContext();
  const { isSuperAdmin, isLoading } = useSuperAdmin();

  if (!isSuperAdmin || isLoading || memoryUsage === null) {
    return null;
  }

  const pct = Math.round(memoryUsage * 100);
  const isHigh = memoryUsage >= 0.7;
  const isCritical = memoryUsage >= 0.85;

  const handleClick = () => {
    const removed = forceCleanup();
    toast.success(`forceCleanup: removed ${removed} cached queries`);
  };

  return (
    <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={handleClick}
            className="fixed bottom-4 right-4 z-[9998] flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-mono shadow-md backdrop-blur-sm transition-opacity hover:opacity-80"
            style={{
              backgroundColor: isCritical
                ? 'rgba(220, 38, 38, 0.9)'
                : isHigh
                  ? 'rgba(234, 179, 8, 0.9)'
                  : 'rgba(0, 0, 0, 0.6)',
              color: 'white',
            }}
            aria-label="Memory usage - click to cleanup cache"
          >
            <span>RAM {pct}%</span>
            <Trash2 className="h-3 w-3" aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-[200px]">
          <p>JS heap: {pct}% used</p>
          <p className="text-muted-foreground text-xs mt-1">Click to run forceCleanup (clear cache)</p>
        </TooltipContent>
      </Tooltip>
  );
};
